import { createContext, useCallback, useEffect, useLayoutEffect, useMemo, useRef, useState, type ReactNode } from 'react';
import useSWR, { mutate } from 'swr';

import { getUserContextAccess, type UserContextAccess } from 'api/userContext';
import useAuth from 'hooks/useAuth';
import { syncPermissionContext, syncTenantHeader } from 'utils/axios';
import { isTenantScopedKey, tenantScopedKey } from 'utils/tenantCacheKey';
import { loadUserContext, saveUserContext } from 'utils/userContextStorage';
import { resolveContextSelection } from 'utils/contextSelection';

export interface SelectedCompanySiteResult {
  companyId: number;
  companyName: string;
  siteId: number | null;
  siteName: string | null;
}

interface TenantSelectionContextValue {
  tenantId: number | null;
  setTenantId: (tenantId: number | null, options?: { forceSync?: boolean }) => void;
  selectedCompanySiteResult: SelectedCompanySiteResult | null;
  setSelectedCompanySiteResult: (value: SelectedCompanySiteResult | null) => void;
  contextAccess: UserContextAccess | undefined;
  contextLoading: boolean;
}

export const TenantSelectionContext = createContext<TenantSelectionContextValue | undefined>(undefined);

function restoreContext(userKey: string | null) {
  const stored = loadUserContext(userKey);
  saveUserContext(userKey, stored);
  syncTenantHeader(stored.tenantId);
  syncPermissionContext(stored.companyId, stored.siteId);
  return {
    userKey,
    tenantId: stored.tenantId,
    selection:
      stored.companyId != null && stored.companyName != null
        ? {
            companyId: stored.companyId,
            companyName: stored.companyName,
            siteId: stored.siteId,
            siteName: stored.siteName
          }
        : null,
    chooseDefaults: false
  };
}

export function TenantSelectionProvider({ children }: { children: ReactNode }) {
  const { user, userNotFound } = useAuth();
  const userKey = user?.id ?? null;
  const [workingContext, setWorkingContext] = useState(() => restoreContext(userKey));
  // Restore before rendering consumers when another identity signs in on the same browser.
  let activeContext = workingContext;
  if (workingContext.userKey !== userKey) {
    activeContext = restoreContext(userKey);
    setWorkingContext(activeContext);
  }
  const { tenantId, selection, chooseDefaults } = activeContext;

  const { data: contextAccess, error } = useSWR<UserContextAccess>(
    // Not fetched for a user Weightrax doesn't know: the 401 would send them back to /login.
    userNotFound ? null : tenantScopedKey(`api/users/me/context::user:${userKey}`, tenantId),
    () => getUserContextAccess(tenantId!),
    { keepPreviousData: false, revalidateOnFocus: true }
  );
  const contextLoading = tenantId != null && contextAccess === undefined && !error;
  const selectedCompanySiteResult = useMemo(
    () => (contextAccess ? resolveContextSelection(contextAccess, selection, chooseDefaults) : null),
    [contextAccess, selection, chooseDefaults]
  );
  const previousScope = useRef<string | null>(null);

  // Commit headers before consumers issue ordinary requests in their effects. Permission and
  // context lookups also name their headers explicitly, so their cache keys always match the scope.
  useLayoutEffect(() => {
    syncTenantHeader(tenantId);
    syncPermissionContext(selectedCompanySiteResult?.companyId ?? null, selectedCompanySiteResult?.siteId ?? null);
  }, [tenantId, selectedCompanySiteResult]);

  useEffect(() => {
    if (contextLoading) return;
    const scope = `${tenantId}:${selectedCompanySiteResult?.companyId ?? '_'}:${selectedCompanySiteResult?.siteId ?? '_'}`;
    if (previousScope.current === scope) return;
    previousScope.current = scope;
    // Requests mounted while context discovery was loading must retry with the resolved headers.
    void mutate(
      (key) =>
        isTenantScopedKey(key) &&
        typeof key === 'string' &&
        !key.startsWith('api/users/me/context') &&
        !key.startsWith('api/users/me/permissions'),
      undefined,
      { revalidate: true }
    );
  }, [tenantId, selectedCompanySiteResult, contextLoading]);

  useEffect(() => {
    if (contextLoading) return;
    saveUserContext(userKey, {
      tenantId,
      companyId: selectedCompanySiteResult?.companyId ?? null,
      companyName: selectedCompanySiteResult?.companyName ?? null,
      siteId: selectedCompanySiteResult?.siteId ?? null,
      siteName: selectedCompanySiteResult?.siteName ?? null
    });
  }, [userKey, tenantId, selectedCompanySiteResult, contextLoading]);

  const setTenantId = useCallback(
    (value: number | null, options?: { forceSync?: boolean }) => {
      if (value === tenantId && !options?.forceSync) return;
      // Header/storage changes precede cache revalidation and the next render's requests.
      syncTenantHeader(value);
      syncPermissionContext(null, null);
      saveUserContext(userKey, { tenantId: value, companyId: null, companyName: null, siteId: null, siteName: null });
      setWorkingContext({ userKey, tenantId: value, selection: null, chooseDefaults: true });
      void mutate(isTenantScopedKey, undefined, { revalidate: true });
    },
    [tenantId, userKey]
  );

  const setSelectedCompanySiteResult = useCallback(
    (value: SelectedCompanySiteResult | null) => {
      const selection = contextAccess ? resolveContextSelection(contextAccess, value, false) : null;
      syncPermissionContext(selection?.companyId ?? null, selection?.siteId ?? null);
      saveUserContext(userKey, {
        tenantId,
        companyId: selection?.companyId ?? null,
        companyName: selection?.companyName ?? null,
        siteId: selection?.siteId ?? null,
        siteName: selection?.siteName ?? null
      });
      setWorkingContext((previous) => ({ ...previous, selection, chooseDefaults: false }));
    },
    [contextAccess, tenantId, userKey]
  );

  const contextValue = useMemo(
    () => ({
      tenantId,
      setTenantId,
      selectedCompanySiteResult,
      setSelectedCompanySiteResult,
      contextAccess,
      contextLoading
    }),
    [tenantId, setTenantId, selectedCompanySiteResult, setSelectedCompanySiteResult, contextAccess, contextLoading]
  );

  return <TenantSelectionContext.Provider value={contextValue}>{children}</TenantSelectionContext.Provider>;
}
